/**
 * Speech Recognition & Synthesis Service
 * Handles voice input and text-to-speech output
 */

class SpeechService {
  constructor() {
    this.recognition = null;
    this.synthesis = window.speechSynthesis;
    this.isListening = false;

    // Initialize speech recognition if available
    if ("webkitSpeechRecognition" in window || "SpeechRecognition" in window) {
      const SpeechRecognition =
        window.SpeechRecognition || window.webkitSpeechRecognition;
      this.recognition = new SpeechRecognition();
      this.recognition.continuous = false;
      this.recognition.interimResults = false;
      this.recognition.lang = "en-US";
    }
  }

  /**
   * Start listening for voice input
   * @param {Function} onResult - Callback when speech is recognized
   * @param {Function} onError - Error callback
   */
  startListening(onResult, onError) {
    if (!this.recognition) {
      onError("Speech recognition not supported in this browser");
      return;
    }

    if (this.isListening) {
      this.stopListening();
      // Wait a bit before restarting
      setTimeout(() => {
        this.startListening(onResult, onError);
      }, 100);
      return;
    }

    try {
      this.recognition.onstart = () => {
        this.isListening = true;
        console.log("Listening...");
      };

      this.recognition.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        this.isListening = false;
        onResult(transcript);
      };

      this.recognition.onerror = (event) => {
        this.isListening = false;
        onError(event.error);
      };

      this.recognition.onend = () => {
        this.isListening = false;
      };

      this.recognition.start();
    } catch (err) {
      console.error("Error starting recognition:", err);
      if (err.name === "InvalidStateError") {
        // Already started, wait and try to stop
        this.isListening = true;
        this.stopListening();
        setTimeout(() => {
          this.startListening(onResult, onError);
        }, 200);
      } else {
        onError(err.message || "Failed to start speech recognition");
      }
    }
  }

  /**
   * Stop listening
   */
  stopListening() {
    if (this.recognition) {
      try {
        if (this.isListening) {
          this.recognition.stop();
        }
        this.isListening = false;
      } catch (err) {
        console.error("Error stopping recognition:", err);
        this.isListening = false;
      }
    }
  }

  /**
   * Speak text using TTS
   * @param {string} text - Text to speak
   * @param {string} lang - Language code (default: 'en-US')
   */
  speak(text, lang = "en-US") {
    // Cancel any ongoing speech
    this.synthesis.cancel();

    if (!text) return;

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = lang;
    utterance.rate = 1.0;
    utterance.pitch = 1.0;
    utterance.volume = 1.0;

    // Try to find a good voice
    const voices = this.synthesis.getVoices();
    const preferredVoices = voices.filter(
      (voice) => voice.lang.startsWith("en") && voice.name.includes("Female")
    );

    if (preferredVoices.length > 0) {
      utterance.voice = preferredVoices[0];
    }

    this.synthesis.speak(utterance);

    return new Promise((resolve) => {
      utterance.onend = () => resolve();
    });
  }

  /**
   * Stop speaking
   */
  stopSpeaking() {
    this.synthesis.cancel();
  }

  /**
   * Check if speech recognition is available
   */
  isAvailable() {
    return this.recognition !== null;
  }
}

// Export singleton instance
const speechService = new SpeechService();
export default speechService;
